import { auditTime, filter, fromEvent, map, startWith, switchMap, take, takeUntil, tap } from 'rxjs';
import { Component, effect, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoaderSpinnerComponent } from '@aston/foundation/components';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { UnsubscriberClass } from '@aston/foundation/classes';
import { LanguageService } from '@aston/foundation/services';
import { Router } from '@angular/router';

import { AiChatButton, chatTrashButton, systemMessage, temporaryText } from '../../functions/utils.ai';
import { AIChatboxService } from '../../services/ai-chatbox.service';
import { AIChatboxSuggestionsService } from '../../services/ai-chatbox-suggestions.service';
import { AIChatboxStylesService } from '../../services/ai-chatbox.styles.service';
import { AIChatInputComponent } from '../ai-chat-input/ai-chat-input.component';
import { AIMessageListComponent } from '../ai-message-list/ai-message-list.component';
import { AIChatToolbarComponent } from '../ai-chat-toolbar/ai-chat-toolbar.component';
import { AIHelpBarComponent } from '../ai-help-bar/ai-help-bar.component';
import { AiChatMessage, createMessageId } from '../../models/ai-chat-message.model';
import { ChatBotResponse } from '../../models/ai-response.model';
import { AI_MESSAGES_INTERCEPTOR } from '../../di';

@Component({
	selector: 'ai-chat-box',
	styles: `
		:host {
			border-left: 1px solid #d5d5d5;
			display: flex;
			height: 99vh;
		}
		.ai-chatbox-body {
			font-family: "Niramit", sans-serif;
			display: flex;
			flex-direction: column;
			flex: 1 1 auto;
			min-height: 0;
			background: #fafbfc;
		}
		.ai-composer {
			flex: 0 0 auto;
			display: flex;
			flex-direction: column;
		}
		.top-bar {
			padding: 11px 40px 0 40px;
			flex-direction: row-reverse;
			display: flex;
			overflow: hidden;
		}
		.top-bar img {
			margin-bottom: -10px;
		}
		.top-bar h3 {
			margin: 20px 0;
		}
	`,
	template: `
		<div class="d-flex flex-column w-100">
			<div class="top-bar">
				<img ngSrc="/assets/images/ava.png" [width]="48" [height]="72" />
				<h3 translate="App.AIChat.Title"></h3>
			</div>
			<ai-chat-help-bar class="position-relative" [beta]="beta()" [insight]="insight()" />
			@if (styles.combined$ | async; as s) {
				<div class="ai-chatbox-body">
					<ai-message-list [messages]="messages()" [avatars]="s.avatarsStyle" [messageStyles]="s.messageStyles" />
					<div class="ai-composer">
						<ai-chat-toolbar [buttons]="allCustomButtons()" />
						<ai-chat-input
							#input
							[placeholder]="s.textInputProps.placeholder"
							[characterLimit]="s.textInputProps.characterLimit"
							[styles]="s.textInputProps.styles"
							(submitted)="send($event)"
						/>
					</div>
				</div>
			} @else {
				<loader-spinner />
			}
		</div>
	`,
	imports: [
		CommonModule,
		NgOptimizedImage,
		TranslateModule,
		LoaderSpinnerComponent,
		AIHelpBarComponent,
		AIMessageListComponent,
		AIChatToolbarComponent,
		AIChatInputComponent,
	],
})
export class AIChatBoxComponent extends UnsubscriberClass {
	suggestions = inject(AIChatboxSuggestionsService)
	styles = inject(AIChatboxStylesService)
	interceptor = inject(AI_MESSAGES_INTERCEPTOR)
	i18n = inject(TranslateService)
	ais = inject(AIChatboxService)
	lang = inject(LanguageService)
	router = inject(Router)
	el = inject(ElementRef)

	builtinsCustomButtons: AiChatButton[] = [
		{
			...chatTrashButton,
			onClick: () => {
				this.messages.set([])
				this.ais.startNewThread()
				this.trash.emit()
			}
		}
	]

	beta = input(false)
	insight = input(false)
	history = input<ChatBotResponse[]>()
	customButtons = input<AiChatButton[]>([])

	trash = output<void>()

	inputRef = viewChild<AIChatInputComponent>('input')

	messages = signal<AiChatMessage[]>([])

	allCustomButtons = () => [...this.builtinsCustomButtons, ...(this.customButtons() || [])]

	// Reflects the history() input into the message list whenever the parent replaces it
	// (eg. super-debtor-detail-recommendations sets it once after loading a saved thread).
	applyHistory = effect(() => {
		const history = this.history()
		if (history?.length) {
			this.messages.set(history.map(item => this.toMessage(item)))
		}
	})

	private toMessage(item: ChatBotResponse & { role?: AiChatMessage['role'] }): AiChatMessage {
		const role = item.role || 'ai'
		return {
			id: createMessageId(),
			role,
			text: item.text,
			html: item.html ?? (role === 'ai' && item.text ? this.hydrateHistoryText(item.text) : item.html),
		}
	}

	// Recovered messages (history/resume) only carry raw text; run them through the same
	// entity-token hydration the live 'done' stream event applies, so badges render consistently.
	private hydrateHistoryText(text: string): string {
		const [hydrated] = this.interceptor.hydrateResponse({ text })
		return this.interceptor.hydrateFullResponse(hydrated.text)
	}

	// Display a welcome message once, and after lang change
	refreshIntro$ = this.lang.language$.pipe(
		startWith(null),
		switchMap(_ => this.ais.intro$),
		auditTime(300),
		tap(msgs => msgs.forEach(html => this.pushMessage(systemMessage({ html })))),
		takeUntil(this.destroySubscriptions$),
	)

	// When page suggestions change, display them
	appendPageSuggestions$ = this.suggestions.pageContextSuggestionsButtons$.pipe(
		auditTime(1000),
		filter(buttons => buttons?.length > 0),
		map(buttons => this.pushMessage(systemMessage({ html: temporaryText(buttons.join('')) }))),
		takeUntil(this.destroySubscriptions$),
	)

	resumeConversation$ = this.ais.resume$.pipe(
		tap(conv => {
			this.messages.set([])
			this.ais.startNewThread()
			setTimeout(() => {
				conv.messages.forEach(msg => {
					this.pushMessage({ role: 'user', text: msg.question })
					this.pushMessage({ role: 'ai', text: msg.answer, html: this.hydrateHistoryText(msg.answer) })
				})
				this.ais.resumeThread({ threadId: conv.threadId, contextId: conv.contextId })
			}, 400)
		}),
		takeUntil(this.destroySubscriptions$),
	)

	closeEntityMenusOnOutsideClick$ = fromEvent<MouseEvent>(document, 'click').pipe(
		filter(e => !(e.composedPath() as HTMLElement[]).some(el => el?.classList?.contains?.('ai-entity-token'))),
		tap(() => this.interceptor.closeAllEntityMenus(this.el.nativeElement)),
		takeUntil(this.destroySubscriptions$),
	)

	constructor() {
		super()
		this.refreshIntro$.subscribe()
		this.appendPageSuggestions$.subscribe()
		this.resumeConversation$.subscribe()
		this.closeEntityMenusOnOutsideClick$.subscribe()

		// ugly hack for chrome browser lack of originalTarget from shadow-dom; also used by
		// the link()/linkButton() html helpers (onclick="window.navigateByUrl(...)") in generated system messages
		;(window as unknown as { navigateByUrl: Router['navigateByUrl'] }).navigateByUrl = this.router.navigateByUrl.bind(this.router)
	}

	send(text: string) {
		this.pushMessage({ role: 'user', text })
		const aiMessage = this.pushMessage({ role: 'ai', text: '', streaming: true, pending: 'thinking' })

		this.ais.sendMessage(text).subscribe(event => {
			if (event.type === 'delta') {
				this.updateMessage(aiMessage.id, m => ({ ...m, pending: 'streaming', text: (m.text || '') + event.response.text }))
			} else if (event.type === 'tool') {
				this.updateMessage(aiMessage.id, m => ({ ...m, pending: { tool: event.toolName } }))
			} else if (event.type === 'done') {
				const [hydrated, tempButtons] = this.interceptor.hydrateResponse({ ...event.response, text: event.fullText })
				const html = this.interceptor.hydrateFullResponse(hydrated.text)
				this.updateMessage(aiMessage.id, m => ({ ...m, html, text: event.fullText, streaming: false, pending: null }))
				this.pushMessage(tempButtons)
			} else if (event.type === 'error') {
				this.styles.errors$.pipe(take(1)).subscribe(errors => {
					this.updateMessage(aiMessage.id, m => ({ ...m, text: errors.default, streaming: false, pending: null }))
				})
			} else if (event.type === 'rejected') {
				this.updateMessage(aiMessage.id, m => ({ ...m, text: event.reason, streaming: false, role: 'system', pending: null }))
			}
		})
	}

	pushMessage(message: Partial<AiChatMessage> & { role: AiChatMessage['role'] }): AiChatMessage {
		const full: AiChatMessage = { id: createMessageId(), ...message }
		this.messages.update(list => [...list, full])
		return full
	}

	updateMessage(id: string, updater: (m: AiChatMessage) => AiChatMessage) {
		this.messages.update(list => list.map(m => m.id === id ? updater(m) : m))
	}

	setInput(content: string) {
		this.inputRef()?.setValue(content)
	}

	getMessages(): AiChatMessage[] {
		return this.messages()
	}

	addMessage(message: ChatBotResponse & { role?: AiChatMessage['role'] }) {
		return this.pushMessage({ role: message.role || 'system', text: message.text, html: message.html })
	}

	submitUserMessage(message: ChatBotResponse | string) {
		const text = typeof message === 'string' ? message : (message.text || '')
		if (text) this.send(text)
	}

	findCustomButtons(selector = '.custom-button') {
		return Array.from((this.el.nativeElement as HTMLElement).querySelectorAll(selector)) as HTMLElement[]
	}
}
